import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";

/**
 * The glass card wrapper. Applies opacity / blur / radius / glow from config
 * via CSS custom properties; tilt behaviour is attached separately by
 * `effects/tilt.ts` so the card component stays purely structural.
 *
 * When opacity or blur hit 0, the corresponding CSS fallbacks (background,
 * backdrop-filter) are set to `transparent` / `none` so the card goes truly
 * invisible rather than leaving a faint saturation glow.
 */
export function mountCard(card: ProfileConfig["card"]): HTMLElement {
  const classes = ["card"];
  if (!card.glowEnabled) classes.push("card--no-glow");
  if (!card.borderEnabled) classes.push("card--no-border");
  if (card.opacity <= 0.001) classes.push("card--transparent");
  if (card.blurPx <= 0) classes.push("card--no-blur");
  if (card.borderAnimation && card.borderAnimation !== "none") {
    classes.push(`card--border-${card.borderAnimation}`);
  }

  // CSS `border-style: double` only renders as two lines when the border
  // is at least 3px wide; below that it silently falls back to solid.
  // Nudge the effective width up so the user's choice actually shows.
  const effectiveBorderWidth =
    card.borderStyle === "double" && (card.borderWidthPx ?? 1) < 3
      ? 3
      : (card.borderWidthPx ?? 1);

  const node = el("article", {
    class: classes.join(" "),
    style: [
      `--card-bg-color:${card.backgroundColor ?? "#12121e"}`,
      `--card-opacity:${card.opacity}`,
      `--card-blur:${card.blurPx}px`,
      `--card-radius:${card.borderRadiusPx}px`,
      `--card-glow:${card.glowColor}`,
      `--card-glow-intensity:${card.glowIntensity ?? 0.5}`,
      `--card-border-color:${card.borderColor ?? "#ffffff"}`,
      `--card-border-width:${effectiveBorderWidth}px`,
      `--card-border-style:${card.borderStyle ?? "solid"}`,
      `--card-pulse-radius:${card.borderPulseRadius ?? 72}px`,
      `--card-enter-duration:${card.openAnimation?.durationMs ?? 500}ms`,
      `--card-enter-delay:${card.openAnimation?.delayMs ?? 0}ms`,
      `--term-fg:${card.terminalColor ?? "#7dff6a"}`,
      `--term-fg-dim:${card.terminalColor ?? "#7dff6a"}88`,
      `--card-glow-intensity:${card.glowIntensity}`,
      `--card-border-color:${card.borderColor}`,
      `--card-border-width:${card.borderWidthPx}px`,
      `--card-border-style:${card.borderStyle}`,
    ].join(";"),
    "data-tilt": card.tilt.enabled ? "on" : "off",
    "data-tilt-mode": card.tilt.mode ?? "3d",
    "data-tilt-max": String(card.tilt.maxDeg),
    "data-tilt-respect-rm": card.tilt.respectReducedMotion ? "1" : "0",
  });

  // The opening animation uses `animation-fill-mode: both` so keyframe 0
  // renders pre-animation (prevents a flash at natural size). That means
  // the animation keeps "filling" its final keyframe transform AFTER it
  // ends — which beats the inline style tilt.ts sets for hover parallax.
  // As soon as any card-enter* animation completes we clear the inline
  // `animation` so tilt's transform wins again.
  //
  // Template-specific variants (`card-enter-classic`, `card-enter-hero`,
  // …) also need cleanup, so match the prefix. Not-once() because blob's
  // border morph also fires animationend repeatedly and we want those to
  // keep looping — the match guards on the name prefix.
  // Clear only the card-enter ONE-SHOT animation so tilt can grab the
  // inline transform again. Infinite border animations (blob, pulse,
  // rainbow, wave) must keep running — we preserve them by setting
  // just `animation-name: none` for the specific entrance animation
  // rather than nuking the whole shorthand.
  //
  // For .card--border-blob which layers card-enter + card-blob-morph via
  // the comma-list form, this means the blob loop survives past 500ms.
  node.addEventListener("animationend", (e) => {
    if (e.target !== node) return;
    if (!e.animationName.startsWith("card-enter")) return;
    // Reset transform-origin so tilt parallax pivots from the card's
    // center. Some templates (e.g. .card--magazine) set special origins
    // for their entrance animation that would otherwise persist and
    // skew the tilt's rotation axis.
    node.style.transformOrigin = "center center";
    // Parse existing animation-name, remove just the ending entrance,
    // re-apply the rest so infinite border animations keep looping.
    const cs = getComputedStyle(node);
    const names = cs.animationName.split(",").map((n) => n.trim());
    const remaining = names.filter((n) => !n.startsWith("card-enter"));
    if (remaining.length === 0) {
      node.style.animation = "none";
    } else {
      // Only "card-enter*" is gone; the card-blob-morph (or similar)
      // keeps its timing via the existing style sheet.
      node.style.animationName = remaining.join(", ");
    }
  });
  return node;
}
